import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createUser,
  deleteUser,
  getAllUsers,
  updateUser,
} from "../../../../../services/userServices";

import { getAllSections } from "../../../../../services/sectionsServices";

import {
  extractArray,
  getStats,
  getUserId,
  isDemoMode,
  normalizeText,
} from "../utils/usersManagerUtils";

const CHUNK = 50;

export default function useUsersManager() {
  const [users, setUsers] = useState([]);
  const [sections, setSections] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const [visibleCount, setVisibleCount] = useState(CHUNK);

  const [drawer, setDrawer] = useState({
    type: null,
    user: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllUsers();
      setUsers(extractArray(data));
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs :", error);
      toast.error("Erreur lors de la récupération des utilisateurs.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSections = useCallback(async () => {
    try {
      const data = await getAllSections();
      setSections(extractArray(data));
    } catch (error) {
      console.error("Erreur lors de la récupération des sections :", error);
      toast.error("Erreur lors de la récupération des sections.");
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchUsers(), fetchSections()]);
  }, [fetchUsers, fetchSections]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const stats = useMemo(() => getStats(users), [users]);

  const filteredUsers = useMemo(() => {
    const term = normalizeText(query.trim());

    const filtered = users.filter((user) => {
      const fullName = normalizeText(`${user.nom || ""} ${user.prenom || ""}`);
      const email = normalizeText(user.email || "");

      const matchesSearch =
        !term || fullName.includes(term) || email.includes(term);

      const matchesRole =
        !selectedRole || String(user.role_id) === String(selectedRole);

      const matchesSection =
        !selectedSection || String(user.section_id) === String(selectedSection);

      return matchesSearch && matchesRole && matchesSection;
    });

    return filtered.sort((a, b) => {
      const nameA = `${a.nom || ""} ${a.prenom || ""}`;
      const nameB = `${b.nom || ""} ${b.prenom || ""}`;

      return nameA.localeCompare(nameB, "fr", { sensitivity: "base" });
    });
  }, [users, query, selectedRole, selectedSection]);

  const visibleUsers = useMemo(() => {
    return filteredUsers.slice(0, visibleCount);
  }, [filteredUsers, visibleCount]);

  const resetFilters = () => {
    setQuery("");
    setSelectedRole("");
    setSelectedSection("");
    setVisibleCount(CHUNK);
  };

  const loadMore = () => {
    setVisibleCount((current) =>
      Math.min(current + CHUNK, filteredUsers.length)
    );
  };

  const openCreateDrawer = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "create",
      user: null,
    });
  };

  const openEditDrawer = (user) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "edit",
      user,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      user: null,
    });
  };

  const handleCreateUser = async (payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await createUser(payload);
      toast.success("Utilisateur créé avec succès.");

      await fetchUsers();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur :", error);
      toast.error("Erreur lors de la création de l'utilisateur.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async (userId, payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await updateUser(userId, payload);
      toast.success("Utilisateur mis à jour avec succès.");

      await fetchUsers();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'utilisateur :", error);
      toast.error("Erreur lors de la mise à jour de l'utilisateur.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    const userId = getUserId(user);

    if (!userId) return;

    const confirmDelete = window.confirm(
      `Supprimer l'utilisateur ${user.prenom || ""} ${user.nom || ""} ?`
    );

    if (!confirmDelete) return;

    try {
      await deleteUser(userId);

      setUsers((current) =>
        current.filter((item) => String(getUserId(item)) !== String(userId))
      );

      toast.success("Utilisateur supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'utilisateur :", error);
      toast.error("Erreur lors de la suppression de l'utilisateur.");
    }
  };

  return {
    users,
    sections,
    loading,
    saving,
    stats,
    query,
    selectedRole,
    selectedSection,
    visibleUsers,
    filteredUsers,
    visibleCount,
    drawer,
    demoMode,

    setQuery,
    setSelectedRole,
    setSelectedSection,
    resetFilters,
    loadMore,
    refreshData,

    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
  };
}